import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAgent, useCreateAgent, useUpdateAgent, useAgentsForSubAgentSelection } from '../../hooks/useAgents';
import { useSkills } from '../../hooks/useSkills';
import { useGroupedTools } from '../../hooks/useTools';
import { useModels, stripProfilePrefix } from '../../hooks/useModels';
import { useAllKnowledgeBases } from '../../hooks/useKnowledgeBases';
import { useSpaceId } from '../../contexts/SpaceContext';
import { useAuth } from '../../store/auth-context';
import { useOrgCredentials } from '../../hooks/useIdentityCredentials';
import { hasConnectedVoiceCredential } from '../../voice/use-voice-availability';
import { routes } from '../../lib/routes';
import { Badge, Button, Card, CardHeader, Input, Label, Select, Switch, Textarea, MarkdownEditor } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { getGroupLabel } from '../../utils/tool-groups';
import { AttachSearch, type AttachOption } from '../../components/agents/AttachSearch';
import { AttachedCard } from '../../components/agents/AttachedCard';
import type { CreateAgentInput, UpdateAgentInput, AgentRuntime, PlatformModel, ModelCapability, KnowledgeBaseGrant, Tool } from '../../types';
import { Server, Terminal, type LucideIcon } from 'lucide-react';

const CAPABILITY_BADGE_VARIANT: Record<ModelCapability, BadgeVariant> = {
  text: 'gray',
  vision: 'teal',
  reasoning: 'purple',
  code: 'info',
};

// Grok Voice built-in voices. The Radix-based Select forbids empty-string
// item values (it throws and takes the whole form down with it), so the
// provider-default choice uses a sentinel that maps back to '' in state.
// TODO(voice): confirm the voice list against current xAI docs before GA.
const VOICE_DEFAULT_SENTINEL = 'default';
const GROK_VOICE_OPTIONS = [
  { value: VOICE_DEFAULT_SENTINEL, label: 'Default voice' },
  { value: 'ara', label: 'Ara' },
  { value: 'eve', label: 'Eve' },
  { value: 'rex', label: 'Rex' },
  { value: 'sal', label: 'Sal' },
  { value: 'leo', label: 'Leo' },
];

const RUNTIME_OPTIONS: {
  value: AgentRuntime;
  label: string;
  description: string;
  Icon: LucideIcon;
}[] = [
  {
    value: 'agentcore',
    label: 'AgentCore',
    description: 'Managed Bedrock AgentCore runtime. Best for most agents.',
    Icon: Server,
  },
  {
    value: 'claude',
    label: 'Claude',
    description: 'Full-environment agent with bash, file access, and package installs.',
    Icon: Terminal,
  },
];

/**
 * Credential status line under the Voice switch. Voice sessions need an xAI
 * key somewhere in the backend's user → space → org tier walk; there's no
 * save-time validation (deliberate), so this is the only place the form can
 * warn. Org admins get a live org-tier check (`useOrgCredentials` refreshes
 * on `optr.identity.*` events); non-admins can't see the org tier, so they
 * get the static requirement note.
 */
function VoiceCredentialStatus() {
  const { user } = useAuth();
  const orgCredentials = useOrgCredentials();
  if (user?.role !== 'admin') {
    return (
      <p className="text-xs text-muted-foreground">
        Requires an xAI API key (organization, space, or personal) — sessions
        fail at start without one.
      </p>
    );
  }
  if (!orgCredentials.isFetched) return null;
  if (hasConnectedVoiceCredential(orgCredentials.data)) {
    return <p className="text-xs text-emerald-500">xAI key connected ✓</p>;
  }
  return (
    <p className="text-xs text-amber-500">
      No org xAI key — voice sessions will fail.{' '}
      <Link
        to={`${routes.console.credentials}?scope=org`}
        className="font-medium underline underline-offset-2 hover:opacity-80"
      >
        Connect one →
      </Link>
    </p>
  );
}

export default function AgentForm() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const isEditing = !!agentId;

  const { data: existingAgent, isLoading: agentLoading } = useAgent(agentId);
  const { data: skillsData } = useSkills();
  const { data: groupedToolsData } = useGroupedTools();
  const { data: platformModels } = useModels();
  const { agents: availableSubAgents } = useAgentsForSubAgentSelection(agentId);
  // Cross-space KB list (`/me/kb`) — the agent's KB grants are global kbIds,
  // and the space-scoped `/kb` endpoint can't be told which space (the URL
  // `:spaceId` isn't propagated to API calls), so it resolves the backend's
  // default space and hides KBs the user can otherwise grant. Use the same
  // aggregator the KB list page uses so every grantable KB shows up.
  const { items: availableKbs } = useAllKnowledgeBases();

  const createAgent = useCreateAgent();
  const updateAgent = useUpdateAgent();

  const modelByProviderId = useMemo(() => {
    const map: Record<string, PlatformModel> = {};
    for (const m of platformModels ?? []) {
      map[m.providerModelId] = m;
    }
    return map;
  }, [platformModels]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    modelId: '',
    runtime: 'agentcore' as AgentRuntime,
    systemPrompt: '',
    temperature: '',
    maxTokens: '',
    maxIterations: '',
    skillIds: [] as string[],
    toolIds: [] as string[],
    subAgentIds: [] as string[],
    knowledgeBases: [] as KnowledgeBaseGrant[],
    enableCodeInterpreter: false,
    enableCaching: false,
    enableAui: false,
    enableAskUser: true,
    enableVoice: false,
    voiceId: '',
    voiceLanguage: '',
    voiceMaxSessionMinutes: '',
  });

  // Legacy/other-env agents may store a profile-prefixed id (e.g. us.anthropic.…);
  // resolve the catalog entry and drive the dropdown off the bare id.
  const selectedModelId = stripProfilePrefix(formData.modelId);

  const modelOptions = useMemo(
    () =>
      (platformModels ?? [])
        .filter((m) => m.status !== 'deprecated' || m.providerModelId === selectedModelId)
        .map((m) => ({
          value: m.providerModelId,
          label: m.status === 'preview' ? `${m.displayName} (Preview)` : m.displayName,
        })),
    [platformModels, selectedModelId],
  );

  const handleModelChange = (newModelId: string) => {
    setFormData((prev) => ({
      ...prev,
      modelId: newModelId,
      temperature: '',
      maxTokens: '',
    }));
  };

  // Set default model once models load (only for new agents)
  useEffect(() => {
    if (!isEditing && !formData.modelId && modelOptions.length > 0) {
      setFormData((prev) => ({ ...prev, modelId: modelOptions[0].value }));
    }
  }, [isEditing, formData.modelId, modelOptions]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const skills = skillsData?.items || [];
  const toolGroups = groupedToolsData?.groups || [];

  // Flat lookup so attached tool cards can resolve a toolId regardless of group.
  const toolsById = useMemo(() => {
    const map = new Map<string, Tool>();
    for (const group of toolGroups) {
      for (const tool of group.tools) map.set(tool.toolId, tool);
    }
    return map;
  }, [toolGroups]);

  // Search options (only items not yet attached) for each section.
  const skillOptions = useMemo<AttachOption[]>(
    () =>
      skills
        .filter((s) => !formData.skillIds.includes(s.skillId))
        .map((s) => ({ id: s.skillId, label: s.name, description: s.description })),
    [skills, formData.skillIds],
  );

  const toolOptions = useMemo<AttachOption[]>(
    () =>
      toolGroups.flatMap((group) =>
        group.tools
          .filter((t) => !formData.toolIds.includes(t.toolId))
          .map((t) => ({
            id: t.toolId,
            label: t.name,
            description: t.description,
            group: group.group,
            groupLabel: getGroupLabel(group.group),
          })),
      ),
    [toolGroups, formData.toolIds],
  );

  const subAgentOptions = useMemo<AttachOption[]>(
    () =>
      availableSubAgents
        .filter((a) => !formData.subAgentIds.includes(a.agentId))
        .map((a) => ({ id: a.agentId, label: a.name, description: a.description })),
    [availableSubAgents, formData.subAgentIds],
  );

  const kbOptions = useMemo<AttachOption[]>(
    () =>
      availableKbs
        .filter((kb) => !formData.knowledgeBases.some((g) => g.kbId === kb.kbId))
        .map((kb) => ({
          id: kb.kbId,
          label: kb.name,
          description: kb.description,
          badge:
            kb.scope === 'org'
              ? { text: 'org', variant: 'purple' as BadgeVariant }
              : undefined,
        })),
    [availableKbs, formData.knowledgeBases],
  );

  useEffect(() => {
    if (existingAgent && isEditing) {
      setFormData({
        name: existingAgent.name,
        description: existingAgent.description || '',
        modelId: existingAgent.modelId,
        runtime: existingAgent.runtime ?? 'agentcore',
        systemPrompt: existingAgent.systemPrompt,
        temperature: existingAgent.temperature?.toString() || '',
        maxTokens: existingAgent.maxTokens?.toString() || '',
        maxIterations: existingAgent.maxIterations?.toString() || '',
        skillIds: existingAgent.skillIds || [],
        toolIds: existingAgent.toolIds || [],
        subAgentIds: existingAgent.subAgentIds || [],
        knowledgeBases: existingAgent.knowledgeBases || [],
        enableCodeInterpreter: existingAgent.enableCodeInterpreter ?? false,
        enableCaching: existingAgent.enableCaching ?? false,
        enableAui: existingAgent.enableAui ?? false,
        enableAskUser: existingAgent.enableAskUser ?? true,
        enableVoice: existingAgent.enableVoice ?? false,
        voiceId: existingAgent.voice?.voiceId ?? '',
        voiceLanguage: existingAgent.voice?.language ?? '',
        voiceMaxSessionMinutes: existingAgent.voice?.maxSessionMinutes?.toString() ?? '',
      });
    }
  }, [existingAgent, isEditing]);

  const selectedModel = modelByProviderId[selectedModelId] as PlatformModel | undefined;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.systemPrompt.trim()) {
      newErrors.systemPrompt = 'System prompt is required';
    }

    if (formData.temperature) {
      const temp = parseFloat(formData.temperature);
      const ps = selectedModel?.parameterSupport.temperature;
      const min = ps?.min ?? 0;
      const max = ps?.max ?? 1;
      if (isNaN(temp) || temp < min || temp > max) {
        newErrors.temperature = `Temperature must be between ${min} and ${max}`;
      }
    }

    if (formData.maxTokens) {
      const tokens = parseInt(formData.maxTokens);
      const ps = selectedModel?.parameterSupport.maxTokens;
      const min = ps?.min ?? 1;
      const max = ps?.max;
      if (isNaN(tokens) || tokens < min || (max != null && tokens > max)) {
        newErrors.maxTokens = max
          ? `Max tokens must be between ${min} and ${max}`
          : `Max tokens must be at least ${min}`;
      }
    }

    if (formData.maxIterations) {
      // No upper bound — the backend does not cap iterations, so the form
      // must not either. Only guard against non-numeric / sub-1 input.
      const iterations = parseInt(formData.maxIterations);
      if (isNaN(iterations) || iterations < 1) {
        newErrors.maxIterations = 'Max iterations must be at least 1';
      }
    }

    if (formData.enableVoice && formData.voiceMaxSessionMinutes) {
      const mins = parseInt(formData.voiceMaxSessionMinutes);
      if (isNaN(mins) || mins < 1 || mins > 180) {
        newErrors.voiceMaxSessionMinutes = 'Max session minutes must be between 1 and 180';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const selectedModel = modelByProviderId[selectedModelId];
    const data = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      modelProvider: selectedModel?.provider ?? 'bedrock',
      modelId: formData.modelId,
      runtime: formData.runtime,
      systemPrompt: formData.systemPrompt.trim(),
      temperature: formData.temperature ? parseFloat(formData.temperature) : undefined,
      maxTokens: formData.maxTokens ? parseInt(formData.maxTokens) : undefined,
      maxIterations: formData.maxIterations ? parseInt(formData.maxIterations) : undefined,
      skillIds: formData.skillIds.length > 0 ? formData.skillIds : undefined,
      toolIds: formData.toolIds.length > 0 ? formData.toolIds : undefined,
      subAgentIds: formData.subAgentIds.length > 0 ? formData.subAgentIds : undefined,
      knowledgeBases:
        formData.knowledgeBases.length > 0 ? formData.knowledgeBases : undefined,
      enableCodeInterpreter: formData.enableCodeInterpreter || undefined,
      enableCaching: formData.enableCaching || undefined,
      enableAui: formData.enableAui || undefined,
      // Default-on flag: send the boolean verbatim. `|| undefined` would coerce an
      // explicit `false` to undefined, which the backend reads as "keep default-on".
      enableAskUser: formData.enableAskUser,
      enableVoice: formData.enableVoice || undefined,
      voice: formData.enableVoice
        ? {
            provider: 'grok' as const,
            voiceId: formData.voiceId || undefined,
            language: formData.voiceLanguage.trim() || undefined,
            maxSessionMinutes: formData.voiceMaxSessionMinutes
              ? parseInt(formData.voiceMaxSessionMinutes)
              : undefined,
          }
        : undefined,
    };

    try {
      if (isEditing && agentId) {
        await updateAgent.mutateAsync({
          agentId,
          input: data as UpdateAgentInput,
        });
        success('Agent updated successfully');
        navigate(routes.console.spaceAgent(spaceId, agentId));
      } else {
        const newAgent = await createAgent.mutateAsync(data as CreateAgentInput);
        success('Agent created successfully');
        navigate(routes.console.spaceAgent(spaceId, newAgent.agentId));
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save agent');
    }
  };

  const toggleSkill = (skillId: string) => {
    setFormData((prev) => ({
      ...prev,
      skillIds: prev.skillIds.includes(skillId)
        ? prev.skillIds.filter((id) => id !== skillId)
        : [...prev.skillIds, skillId],
    }));
  };

  const toggleTool = (toolId: string) => {
    setFormData((prev) => ({
      ...prev,
      toolIds: prev.toolIds.includes(toolId)
        ? prev.toolIds.filter((id) => id !== toolId)
        : [...prev.toolIds, toolId],
    }));
  };

  const addToolGroup = (groupKey: string) => {
    const group = toolGroups.find((g) => g.group === groupKey);
    if (!group) return;
    setFormData((prev) => ({
      ...prev,
      toolIds: [...new Set([...prev.toolIds, ...group.tools.map((t) => t.toolId)])],
    }));
  };

  const toggleSubAgent = (subAgentId: string) => {
    setFormData((prev) => ({
      ...prev,
      subAgentIds: prev.subAgentIds.includes(subAgentId)
        ? prev.subAgentIds.filter((id) => id !== subAgentId)
        : [...prev.subAgentIds, subAgentId],
    }));
  };

  const toggleKnowledgeBase = (kbId: string) => {
    setFormData((prev) => {
      const has = prev.knowledgeBases.some((g) => g.kbId === kbId);
      return {
        ...prev,
        knowledgeBases: has
          ? prev.knowledgeBases.filter((g) => g.kbId !== kbId)
          : [...prev.knowledgeBases, { kbId, write: false }],
      };
    });
  };

  const setKnowledgeBaseWrite = (kbId: string, write: boolean) => {
    setFormData((prev) => ({
      ...prev,
      knowledgeBases: prev.knowledgeBases.map((g) =>
        g.kbId === kbId ? { ...g, write } : g,
      ),
    }));
  };

  if (isEditing && agentLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const isSubmitting = createAgent.isPending || updateAgent.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {isEditing ? 'Edit Agent' : 'Create Agent'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isEditing
            ? 'Update your agent configuration'
            : 'Configure a new AI agent'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <Card>
          <CardHeader title="Basic Information" />
          <div className="space-y-4">
            <Input
              label="Name"
              required
              value={formData.name}
              onChange={(e: { target: { value: string } }) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              error={errors.name}
              placeholder="My Agent"
            />
            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e: { target: { value: string } }) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="What does this agent do?"
              rows={2}
            />
          </div>
        </Card>

        {/* Runtime */}
        <Card>
          <CardHeader
            title="Runtime"
            description="Choose the backend that executes this agent."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            {RUNTIME_OPTIONS.map(({ value, label, description, Icon }) => {
              const active = formData.runtime === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, runtime: value }))
                  }
                  aria-pressed={active}
                  className={`flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors ${
                    active
                      ? 'border-primary bg-primary/5 ring-2 ring-primary'
                      : 'border-border bg-card hover:bg-muted'
                  }`}
                >
                  <Icon
                    className={`mt-0.5 h-5 w-5 shrink-0 ${active ? 'text-primary' : 'text-muted-foreground'}`}
                  />
                  <div className="min-w-0">
                    <span
                      className={`block text-sm font-medium ${active ? 'text-primary' : 'text-foreground'}`}
                    >
                      {label}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {description}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Model configuration */}
        <Card>
          <CardHeader title="Model Configuration" />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Select
                label="Model"
                options={modelOptions}
                value={selectedModelId}
                onChange={(e: { target: { value: string } }) =>
                  handleModelChange(e.target.value)
                }
              />
              {selectedModel && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {selectedModel.capabilities
                    .filter((c) => c !== 'text')
                    .map((cap) => (
                      <Badge key={cap} variant={CAPABILITY_BADGE_VARIANT[cap]}>
                        {cap.charAt(0).toUpperCase() + cap.slice(1)}
                      </Badge>
                    ))}
                  <span className="text-xs text-muted-foreground">
                    {Math.round(selectedModel.contextLimit / 1000)}K context
                  </span>
                  {selectedModel.status === 'deprecated' && (
                    <Badge variant="warning">Deprecated</Badge>
                  )}
                </div>
              )}
            </div>
            {selectedModel?.parameterSupport.temperature?.supported && (
              <Input
                label="Temperature"
                type="number"
                step="0.1"
                min={selectedModel.parameterSupport.temperature.min ?? 0}
                max={selectedModel.parameterSupport.temperature.max ?? 1}
                value={formData.temperature}
                onChange={(e: { target: { value: string } }) =>
                  setFormData((prev) => ({ ...prev, temperature: e.target.value }))
                }
                error={errors.temperature}
                hint={`${selectedModel.parameterSupport.temperature.min ?? 0} = deterministic, ${selectedModel.parameterSupport.temperature.max ?? 1} = creative`}
                placeholder={String(selectedModel.parameterSupport.temperature.default ?? 0.7)}
              />
            )}
            <Input
              label="Max Tokens"
              type="number"
              min={selectedModel?.parameterSupport.maxTokens?.min ?? 1}
              max={selectedModel?.parameterSupport.maxTokens?.max}
              value={formData.maxTokens}
              onChange={(e: { target: { value: string } }) =>
                setFormData((prev) => ({ ...prev, maxTokens: e.target.value }))
              }
              error={errors.maxTokens}
              placeholder={String(selectedModel?.parameterSupport.maxTokens?.default ?? 4096)}
            />
            <Input
              label="Max Iterations"
              type="number"
              min="1"
              value={formData.maxIterations}
              onChange={(e: { target: { value: string } }) =>
                setFormData((prev) => ({ ...prev, maxIterations: e.target.value }))
              }
              error={errors.maxIterations}
              hint="Maximum tool-use cycles per run (default: 20)"
              placeholder="20"
            />
          </div>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader
            title="Features"
            description="Enable optional agent capabilities"
          />
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enableCodeInterpreter">Code Interpreter</Label>
                <p className="text-sm text-muted-foreground">
                  Sandboxed code execution, commands, and file management
                </p>
              </div>
              <Switch
                id="enableCodeInterpreter"
                checked={formData.enableCodeInterpreter}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, enableCodeInterpreter: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enableCaching">Prompt Caching</Label>
                <p className="text-sm text-muted-foreground">
                  Bedrock prompt caching for cost and latency optimization
                </p>
              </div>
              <Switch
                id="enableCaching"
                checked={formData.enableCaching}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, enableCaching: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enableAui">AUI Canvas</Label>
                <p className="text-sm text-muted-foreground">
                  Agent can create and manage UI components in a canvas
                </p>
              </div>
              <Switch
                id="enableAui"
                checked={formData.enableAui}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, enableAui: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enableAskUser">Ask User</Label>
                <p className="text-sm text-muted-foreground">
                  Let this agent pause and ask the user questions mid-run.
                </p>
              </div>
              <Switch
                id="enableAskUser"
                checked={formData.enableAskUser}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, enableAskUser: checked }))
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enableVoice">Voice Conversations</Label>
                <p className="text-sm text-muted-foreground">
                  Realtime speech with this agent (requires an xAI key).
                </p>
              </div>
              <Switch
                id="enableVoice"
                checked={formData.enableVoice}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, enableVoice: checked }))
                }
              />
            </div>
            {formData.enableVoice && (
              <div className="ml-4 space-y-4 border-l-2 border-border pl-4">
                <VoiceCredentialStatus />
                <div className="grid gap-4 md:grid-cols-3">
                  <Select
                    label="Voice"
                    options={GROK_VOICE_OPTIONS}
                    value={formData.voiceId || VOICE_DEFAULT_SENTINEL}
                    onChange={(e: { target: { value: string } }) =>
                      setFormData((prev) => ({
                        ...prev,
                        voiceId: e.target.value === VOICE_DEFAULT_SENTINEL ? '' : e.target.value,
                      }))
                    }
                  />
                  <Input
                    label="Language"
                    value={formData.voiceLanguage}
                    onChange={(e: { target: { value: string } }) =>
                      setFormData((prev) => ({ ...prev, voiceLanguage: e.target.value }))
                    }
                    hint="BCP-47 tag, e.g. en-US. Leave empty to auto-detect."
                    placeholder="Auto"
                  />
                  <Input
                    label="Max Session Minutes"
                    type="number"
                    min="1"
                    max="180"
                    value={formData.voiceMaxSessionMinutes}
                    onChange={(e: { target: { value: string } }) =>
                      setFormData((prev) => ({
                        ...prev,
                        voiceMaxSessionMinutes: e.target.value,
                      }))
                    }
                    error={errors.voiceMaxSessionMinutes}
                    placeholder="30"
                  />
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* System prompt */}
        <Card>
          <CardHeader
            title="System Prompt"
            description="Instructions that define your agent's behavior"
          />
          <MarkdownEditor
            value={formData.systemPrompt}
            onChange={(val) =>
              setFormData((prev) => ({ ...prev, systemPrompt: val }))
            }
            error={errors.systemPrompt}
            placeholder="You are a helpful assistant..."
            minHeight="200px"
            suppressHtmlProcessing
          />
        </Card>

        {/* Skills */}
        <Card>
          <CardHeader
            title="Skills"
            description="Search to attach skills this agent can use"
          />
          {skills.length === 0 ? (
            <p className="text-muted-foreground">
              No skills available.{' '}
              <Link to={routes.console.spaceSkillNew(spaceId)} className="text-primary-600 hover:text-primary-500">
                Create a skill
              </Link>
            </p>
          ) : (
            <div className="space-y-3">
              <AttachSearch
                placeholder="Search skills to add…"
                options={skillOptions}
                onAdd={toggleSkill}
              />
              {formData.skillIds.length === 0 ? (
                <p className="text-sm text-muted-foreground">No skills attached yet.</p>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {formData.skillIds.map((id) => {
                    const skill = skills.find((s) => s.skillId === id);
                    return (
                      <AttachedCard
                        key={id}
                        title={
                          <span className="truncate font-medium text-foreground">
                            {skill?.name ?? id}
                          </span>
                        }
                        description={skill?.description}
                        onRemove={() => toggleSkill(id)}
                        removeLabel="Remove skill"
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Tools */}
        <Card>
          <CardHeader
            title="Tools"
            description="Search to attach individual tools, or add a whole group"
          />
          {toolGroups.length === 0 ? (
            <p className="text-muted-foreground">
              No tools available.{' '}
              <Link to={routes.console.spaceToolNew(spaceId)} className="text-primary-600 hover:text-primary-500">
                Create a tool
              </Link>
            </p>
          ) : (
            <div className="space-y-3">
              <AttachSearch
                placeholder="Search tools to add…"
                options={toolOptions}
                onAdd={toggleTool}
                grouped
                onAddGroup={addToolGroup}
              />
              {formData.toolIds.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tools attached yet.</p>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {formData.toolIds.map((id) => {
                    const tool = toolsById.get(id);
                    return (
                      <AttachedCard
                        key={id}
                        title={
                          <span className="truncate font-medium text-foreground">
                            {tool?.name ?? id}
                          </span>
                        }
                        description={tool?.description}
                        badge={
                          <Badge variant="gray">
                            {getGroupLabel(tool?.group || 'other')}
                          </Badge>
                        }
                        onRemove={() => toggleTool(id)}
                        removeLabel="Remove tool"
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Sub-Agents */}
        <Card>
          <CardHeader
            title="Sub-Agents"
            description="Search to attach agents this agent can delegate to"
          />
          {availableSubAgents.length === 0 ? (
            <p className="text-muted-foreground">
              No other agents available for sub-agent delegation.
            </p>
          ) : (
            <div className="space-y-3">
              <AttachSearch
                placeholder="Search agents to add…"
                options={subAgentOptions}
                onAdd={toggleSubAgent}
              />
              {formData.subAgentIds.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sub-agents attached yet.</p>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {formData.subAgentIds.map((id) => {
                    const sub = availableSubAgents.find((a) => a.agentId === id);
                    return (
                      <AttachedCard
                        key={id}
                        title={
                          <span className="truncate font-medium text-foreground">
                            {sub?.name ?? id}
                          </span>
                        }
                        description={sub?.description}
                        onRemove={() => toggleSubAgent(id)}
                        removeLabel="Remove sub-agent"
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Knowledge Bases */}
        <Card>
          <CardHeader
            title="Knowledge Bases"
            description="Grant the agent read (and optionally write) access to KBs. Attaching grants provisions the kb_list, kb_search, and kb_files tools — kb_files_write only on rows with write enabled."
          />
          {availableKbs.length === 0 ? (
            <p className="text-muted-foreground">
              No knowledge bases available.{' '}
              <Link to={routes.operator.knowledge} className="text-primary-600 hover:text-primary-500">
                Create one
              </Link>
            </p>
          ) : (
            <div className="space-y-3">
              <AttachSearch
                placeholder="Search knowledge bases to add…"
                options={kbOptions}
                onAdd={toggleKnowledgeBase}
              />
              {formData.knowledgeBases.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No knowledge bases attached yet.
                </p>
              ) : (
                <div className="grid gap-2 md:grid-cols-2">
                  {formData.knowledgeBases.map((grant) => {
                    const kb = availableKbs.find((k) => k.kbId === grant.kbId);
                    return (
                      <AttachedCard
                        key={grant.kbId}
                        title={
                          <span className="truncate font-medium text-foreground">
                            {kb?.name ?? grant.kbId}
                          </span>
                        }
                        description={kb?.description}
                        badge={
                          kb?.scope === 'org' ? (
                            <Badge variant="purple">org</Badge>
                          ) : undefined
                        }
                        onRemove={() => toggleKnowledgeBase(grant.kbId)}
                        removeLabel="Remove knowledge base"
                      >
                        <div className="mt-1 flex items-center gap-2">
                          <Switch
                            id={`kb-write-${grant.kbId}`}
                            checked={!!grant.write}
                            onCheckedChange={(next) =>
                              setKnowledgeBaseWrite(grant.kbId, next)
                            }
                          />
                          <Label htmlFor={`kb-write-${grant.kbId}`} className="text-xs">
                            Allow write
                          </Label>
                        </div>
                      </AttachedCard>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link to={isEditing ? routes.console.spaceAgent(spaceId, agentId!) : routes.console.spaceAgents(spaceId)}>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={isSubmitting}>
            {isEditing ? 'Save Changes' : 'Create Agent'}
          </Button>
        </div>
      </form>
    </div>
  );
}
